import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  Hourglass,
  ShieldAlert,
  Scale,
  Wallet,
  CalendarClock,
  Building2,
  HeartPulse,
  Snowflake,
  GitMerge,
  TrendingDown,
  CheckCircle2,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import SupportTimelineDiagram from '@/components/diagrams/SupportTimelineDiagram';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const KEY_FACTS = [
  {
    icon: Hourglass,
    title: 'Sustaining Support — what it actually is',
    detail:
      'Per Oracle\'s Lifetime Support Policy, Sustaining Support follows Extended Support and continues for as long as Oracle offers the version. You retain access to the existing knowledge base and previously published patches, but Oracle does not produce new critical patch updates, new security alerts, new tax/legal/regulatory updates, or new certifications against newer operating systems, browsers, or database releases.',
  },
  {
    icon: Scale,
    title: 'Market Driven Support — the in-between option',
    detail:
      'Market Driven Support is a separately negotiated, yearly-renewable program that extends critical patches and security updates beyond the Extended Support window. It typically costs more per year than Extended Support and is approved by Oracle on a case-by-case basis — it is not a universal entitlement.',
  },
  {
    icon: CalendarClock,
    title: 'The decision window',
    detail:
      'WebCenter 12c Premier Support ends December 2026 and Extended Support ends December 2027. Customers considering Sustaining Support need to decide their wait-it-out posture in 2026-2027, before the cost and risk profile changes materially.',
  },
];

const SCENARIOS = [
  {
    icon: Wallet,
    title: 'Budget delayed past the Dec 2026/2027 window',
    body:
      'Capital budget for an upgrade or modernization slipped a fiscal year. Finance has signaled a 12-18 month delay, not a multi-year freeze. The wait-it-out window is short and the replacement decision is already framed.',
  },
  {
    icon: GitMerge,
    title: 'Mid-replacement project that will not finish in time',
    body:
      'A WebCenter replacement (Path B modernization, ERP replatform, or 14c upgrade) is already in flight but the cutover lands in 2028 or later. Sustaining or Market Driven becomes a bridge to the known end state, not an open-ended posture.',
  },
  {
    icon: HeartPulse,
    title: 'Regulated industry requiring continuous critical patches',
    body:
      'Financial services, healthcare, or other regulated environments where the security patch cadence is itself a compliance obligation. Sustaining Support does not produce new critical patches, so this scenario almost always pushes the conversation toward Market Driven Support or an accelerated forward path.',
  },
  {
    icon: Snowflake,
    title: 'Multi-year IT freeze',
    body:
      'Merger integration, ERP replatform consuming all change capacity, or executive directive to hold the AP stack steady for several years. The wait-it-out math degrades quickly past month 24 — every additional month compounds operational drag without any of the offsetting benefit a replacement project would deliver.',
  },
];

const FAQS = [
  {
    q: 'What is Oracle Sustaining Support, exactly?',
    a: 'Sustaining Support is the post-Extended-Support tier of Oracle\'s Lifetime Support Policy that continues for as long as the product remains in Oracle\'s portfolio. You keep access to the existing My Oracle Support knowledge base, previously published patches and updates, and the ability to log service requests. You do not receive new critical patch updates, new security alerts, new tax/legal/regulatory updates, or new certifications against newer operating systems, browsers, or database versions.',
  },
  {
    q: 'How much does Market Driven Support cost compared with Extended Support?',
    a: 'Market Driven Support is typically priced materially higher than Extended Support on a per-year basis, and is renewed yearly. The exact uplift depends on Oracle\'s case-by-case approval and the customer\'s overall Oracle support footprint, and it is not a published list price. Customers seriously considering this option should request a formal quote from Oracle through their account team rather than budgeting against rumored numbers.',
  },
  {
    q: 'Can we get critical security patches under Sustaining Support?',
    a: 'No new critical security patches are produced under Sustaining Support — only patches already published during Premier or Extended Support remain available. You can apply any critical patch update that was already published while the product was in Premier or Extended Support, but Oracle stops generating new ones once Sustaining begins. For customers who need a continuous flow of critical patches past December 2027, the options are Market Driven Support (if Oracle approves it for your case), an upgrade to a supported release (14c), or a modernization path that retires WebCenter as the AP layer.',
  },
  {
    q: 'What about regulatory requirements like SOX, HIPAA, or PCI?',
    a: 'Regulatory frameworks rarely name Sustaining Support specifically, but most require evidence that critical vulnerabilities are remediated within a defined window — which Sustaining Support does not guarantee. Under Sustaining Support, if a new CVE is published against a 12c component, no Oracle-produced patch will arrive. Internal audit and external auditors will ask the same question: what is the remediation path? Some customers answer this with Market Driven Support; others use it as the trigger to accelerate the replacement project.',
  },
  {
    q: 'What are the browser and operating system compatibility risks?',
    a: 'Sustaining Support produces no new certifications, so WebCenter 12c is not re-certified as Windows, macOS, and major browsers release new versions. The pattern we hear most often is the Verifier queue in WebCenter Forms Recognition becoming difficult to run as IT rolls out a new Windows build and the older browser certification matrix degrades. Mobile invoice approval is another common pressure point. Customers tend to surface these compatibility issues 12-24 months into a Sustaining posture.',
  },
  {
    q: 'What are the hidden operational costs of staying on Sustaining Support?',
    a: 'The hidden costs accumulate across several lines and typically exceed the cost of upgrading or modernizing past the 24-month mark. Contractor rates for any change request rise because fewer engineers actively work on an unsupported version; internal IT spends more time on workarounds and one-off fixes; compatibility issues with adjacent systems (browsers, identity providers, database versions) generate their own remediation projects; and the institutional WebCenter knowledge on the customer side typically erodes faster than expected.',
  },
  {
    q: 'Are there hybrid bridge options between Sustaining Support and a full forward path?',
    a: 'Yes — the most common pattern is a planned bridge using a short Market Driven Support window (12-24 months) that keeps critical patches flowing while a 14c upgrade or modernization project runs in parallel. Another pattern is partial cutover — moving the highest-volume AP workflows onto a modern platform first, leaving lower-volume or less-critical workflows on 12c under Sustaining until the second wave. Either pattern materially improves the wait-it-out math by capping the Sustaining window at a known end date.',
  },
  {
    q: 'How is the Decision Call structured?',
    a: 'The Decision Call is a 30-minute call with Andrew Blackman, founder of EZ Cloud and a 25-year Oracle WebCenter specialist. The structure: 5 minutes on your current 12c implementation and stack constraints, 10 minutes on your wait-it-out window and the trigger that opens the decision, 10 minutes on the realistic forward-path options for your situation (Sustaining only, Sustaining + Market Driven bridge, 14c upgrade, or modernization off WebCenter), and 5 minutes on next steps. Output is a plain summary of where the math works and where it breaks for your specific case.',
  },
];

const ENGAGEMENT_TIERS = [
  {
    label: 'Tier 1 · Wait-It-Out Math Review',
    title: 'Fixed-fee read of the cost and risk profile',
    body:
      'Andrew reads your existing 12c WebCenter implementation, your wait-it-out window, and the operational and compliance constraints that frame the decision. Output: a structured assessment of where Sustaining Support works, where it breaks, and what the realistic forward-path options are. Plan is yours to keep regardless of which delivery partner you choose.',
  },
  {
    label: 'Tier 2 · Bridge Plan Architecture Review',
    title: 'One-week read for a Sustaining or Market Driven bridge',
    body:
      'A one-week fixed-fee architecture review focused on the bridge scenario — Sustaining Support or Market Driven Support layered over a defined 12-24 month window while the replacement project runs in parallel. Covers Forms Recognition rules, SOA composites, WebLogic configuration, ADF customizations, EBS / Fusion integration, and what specifically degrades over the bridge window.',
  },
  {
    label: 'Tier 3 · Strategic Advisory Retainer',
    title: 'Founder-level guidance through the decision window',
    body:
      'Monthly retainer with Andrew, sized for the customer working through the 2026-2027 decision window with internal stakeholders, procurement, and Oracle. Strategic guidance on Sustaining vs Market Driven trade-offs, bridge planning, and 14c-vs-modernize sequencing through the wait-it-out period.',
  },
  {
    label: 'Tier 4 · Forward Deployed Engineering Bridge',
    title: 'FDE delivery for the bridge window',
    body:
      'For customers who choose a bridge posture and need an embedded engineering presence to keep WebCenter 12c stable, handle compatibility issues as they emerge, and execute the cutover to 14c or to a modernized AP platform at the end of the bridge. Founder-led, scoped to the defined bridge window.',
  },
];

const HIDDEN_COSTS = [
  'Contractor rates rise as fewer engineers actively work on an unsupported release',
  'Internal IT spends more time on workarounds, one-off fixes, and compatibility patches',
  'Adjacent systems (browsers, identity providers, database versions) trigger their own remediation projects',
  'Institutional WebCenter knowledge on the customer side erodes faster than expected',
  'Audit and compliance review time grows as auditors probe the unpatched-version posture',
  'Change requests against 12c become harder to scope because no new certifications exist to anchor against',
];

const WebCenterSustainingSupportDecision = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: 'Oracle WebCenter Sustaining Support — The Wait-It-Out Math | EZ Cloud',
    description:
      "After December 2027, the only Oracle-provided option for WebCenter 12c is Sustaining Support (limited patching) or Market Driven Support (yearly-renewable, higher cost than Extended Support). For customers considering the wait-it-out path, the math is rarely favorable past the 24-month mark.",
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/solutions/webcenter-sustaining-support-decision',
    },
    about: [
      { '@type': 'Thing', name: 'Oracle Sustaining Support' },
      { '@type': 'Thing', name: 'Oracle Market Driven Support' },
      { '@type': 'Thing', name: 'Oracle WebCenter Support' },
      { '@type': 'Thing', name: 'Oracle Lifetime Support Policy' },
      { '@type': 'Thing', name: 'Fusion Middleware 12c' },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Oracle WebCenter Sustaining Support — The Wait-It-Out Math | EZ Cloud"
        description="After December 2027, the only Oracle-provided option for WebCenter 12c is Sustaining Support (limited patching) or Market Driven Support (yearly-renewable, higher cost than Extended Support). For customers considering the wait-it-out path, the math is rarely favorable past the 24-month mark."
        canonical="https://ezcloud.ai/solutions/webcenter-sustaining-support-decision"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'WebCenter Sustaining Support Decision',
            url: 'https://ezcloud.ai/solutions/webcenter-sustaining-support-decision',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Hourglass className="w-4 h-4" aria-hidden />
              Sustaining Support · Wait-it-out math
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Oracle WebCenter Sustaining Support — the wait-it-out math
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              After December 2027 (end of Extended Support), the only Oracle-provided options are
              Sustaining Support (limited patching) and Market Driven Support (yearly-renewable,
              higher cost than Extended). The math for staying past December 2027.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('sustaining-support-hero')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a Wait-It-Out Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What this is
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              Sustaining Support is real and Oracle-provided. The cost and risk profile shifts
              significantly after December 2027.
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Sustaining Support and Market Driven Support are both Oracle-provided options for
                  WebCenter 12c past the December 2027 Extended Support cliff.
                </strong>{' '}
                Sustaining Support continues for as long as Oracle keeps the version in its
                portfolio, but produces no new critical patches, no new security alerts, no new
                tax/legal/regulatory updates, and no new certifications. Market Driven Support is a
                yearly-renewable program — typically priced higher than Extended Support — that
                continues critical patches and security updates, but Oracle approves it on a
                case-by-case basis.
              </p>
              <p>
                For customers considering the wait-it-out path, the question is not whether
                Sustaining Support exists — it does — but whether the cost and risk profile actually
                works for your situation.{' '}
                <strong className="text-gray-900">
                  Short windows (12-24 months) with a defined replacement project on the other side
                  usually pencil out. Multi-year extensions, regulated industries, and stacks with
                  heavy supplier-side change usually do not.
                </strong>{' '}
                Past the 24-month mark, the operational drag of staying on Sustaining typically
                exceeds the cost of just upgrading or modernizing.
              </p>
              <p>
                This page walks through what each option actually includes, when the math works,
                when it breaks, the hidden costs that accumulate over a multi-year wait, and the
                common scenarios we hear from customers in 2026-2027.
              </p>
            </div>
          </div>
        </section>

        {/* Key facts */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The three things to know
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              How Oracle structures support past Extended
            </h2>
            <div className="max-w-5xl mx-auto mb-12">
              <SupportTimelineDiagram />
            </div>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Per Oracle's Lifetime Support Policy. These three facts together define the wait-it-out
              decision frame for every WebCenter 12c customer in 2026-2027.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {KEY_FACTS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-accent" aria-hidden />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">{detail}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What Sustaining Support includes */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              Tier definition
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What Sustaining Support includes
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Sustaining Support continues for the lifetime that Oracle keeps a product in its
                portfolio. It provides access to the existing My Oracle Support knowledge base,
                previously published patches and updates, and the ability to log service requests
                against documented issues.
              </p>
              <p>
                It does <strong className="text-gray-900">not</strong> include new critical patch
                updates, new security alerts, new tax/legal/regulatory updates, or new certifications
                against newer operating systems, browsers, database releases, or Java versions. Once
                the product enters Sustaining, the patch catalog is effectively frozen at the state it
                was in at the end of Extended Support.
              </p>
              <p>
                Pricing is typically a percentage of the equivalent Premier license-equivalent
                support fee on an ongoing basis. Customers should confirm the exact pricing with their
                Oracle account team — the published support pricing model is the canonical source.
              </p>
            </div>
            <div className="mt-8">
              <Link
                to="/solutions/webcenter-12c-end-of-support-decision"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the full 12c end-of-support decision frame
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* What Market Driven Support is */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              Tier definition
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What Market Driven Support is
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Market Driven Support is a separately negotiated Oracle program that extends critical
                patches and security updates beyond the Extended Support window on a yearly-renewable
                basis. It sits between Extended Support (formal lifecycle tier) and Sustaining Support
                (frozen patch catalog).
              </p>
              <p>
                Key characteristics:{' '}
                <strong className="text-gray-900">
                  yearly-renewable rather than multi-year; typically priced higher than Extended
                  Support; and approved by Oracle on a case-by-case basis
                </strong>
                . It is not a universal entitlement — Oracle decides whether to extend the program for
                a specific customer and a specific product based on their overall account
                relationship, the criticality of the workload, and the demonstrated forward plan.
              </p>
              <p>
                Customers seriously considering Market Driven Support should request a formal quote
                through their Oracle account team early — both because the approval cycle takes time
                and because the pricing is not published, so budgeting against rumored numbers tends
                to surprise finance later in the process.
              </p>
            </div>
          </div>
        </section>

        {/* When wait-it-out math works */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              When it works
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              When wait-it-out math works
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Sustaining Support and Market Driven Support both make defensible sense when several
              conditions hold simultaneously:
            </p>
            <Card className="p-7 bg-accent/5 border border-accent/30 shadow-sm">
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                  <span>
                    <strong className="text-gray-900">Short window (12-24 months)</strong> — the
                    Sustaining or Market Driven posture has a defined end date driven by a known
                    replacement project, not an open-ended extension.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                  <span>
                    <strong className="text-gray-900">Low rate of change</strong> in the WebCenter
                    implementation — few new customizations, few new integration requirements, stable
                    workflow configurations.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                  <span>
                    <strong className="text-gray-900">Stable supplier set</strong> — the AP supplier
                    population is not churning rapidly, so the invoice templates and Forms Recognition
                    rules already trained against the existing catalog continue to perform.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                  <span>
                    <strong className="text-gray-900">No compliance-driven security patching
                    requirements</strong> — the regulatory framework permits a documented compensating
                    control rather than requiring a continuous Oracle-produced patch stream.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                  <span>
                    <strong className="text-gray-900">Defined replacement project</strong> already
                    funded and staffed, with a cutover date that lands before the Sustaining-Support
                    posture starts degrading materially.
                  </span>
                </li>
              </ul>
            </Card>
          </div>
        </section>

        {/* When wait-it-out math breaks */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              When it breaks
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              When wait-it-out math breaks
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              The same posture stops working when any of these factors are present:
            </p>
            <Card className="p-7 bg-white border border-red-200 shadow-sm">
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" aria-hidden />
                  <span>
                    <strong className="text-gray-900">Multi-year extension past 2027</strong> — the
                    posture lasts three years or more without a defined replacement date. Operational
                    drag compounds, contractor rates rise, and the institutional knowledge base
                    typically erodes faster than expected.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" aria-hidden />
                  <span>
                    <strong className="text-gray-900">Regulated industry</strong> — financial
                    services, healthcare, or other environments where the security patch cadence is
                    itself a compliance obligation. Sustaining produces no new critical patches, which
                    forces either Market Driven Support or an accelerated forward path.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" aria-hidden />
                  <span>
                    <strong className="text-gray-900">Heavy supplier-side change</strong> — high
                    supplier churn, frequent new invoice templates, or expansion into new geographies
                    means the Forms Recognition rules and capture configurations need ongoing
                    investment that does not pair well with a frozen-version posture.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" aria-hidden />
                  <span>
                    <strong className="text-gray-900">Vendor-side OS and browser
                    deprecations</strong> — Windows 11 Pro, modern Chrome and Edge releases, and
                    newer identity providers periodically break compatibility with older WebLogic
                    browser certifications. The Verifier queue in WebCenter Forms Recognition is a
                    common pressure point as these compatibility gaps emerge.
                  </span>
                </li>
              </ul>
            </Card>
          </div>
        </section>

        {/* Hidden cost */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <TrendingDown className="w-7 h-7 text-accent flex-shrink-0 mt-1" aria-hidden />
              <p className="text-sm font-semibold uppercase tracking-wide text-accent">
                The hidden cost
              </p>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Past 24 months, operational drag exceeds the cost of moving
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed mb-8">
              <p>
                The visible cost of Sustaining Support is the support fee on the invoice from Oracle.
                The hidden cost is everything that accumulates around it — and the math shifts most
                meaningfully somewhere between months 18 and 24 of a Sustaining posture.
              </p>
              <p>
                The hidden cost categories most customers underestimate:
              </p>
            </div>
            <Card className="p-7 bg-gray-50 border border-gray-200 shadow-sm">
              <ul className="space-y-3 text-gray-700">
                {HIDDEN_COSTS.map((c) => (
                  <li key={c} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </Card>
            <div className="mt-8 text-gray-700 leading-relaxed">
              <p>
                The{' '}
                <Link
                  to="/resources/webcenter-tco-calculator"
                  className="font-semibold text-primary hover:text-accent transition-colors"
                >
                  WebCenter TCO Calculator
                </Link>{' '}
                models these categories side-by-side against the 14c upgrade and modernization paths,
                so the comparison is on common ground rather than just the support-line invoice.
              </p>
            </div>
          </div>
        </section>

        {/* Common scenarios */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common scenarios we hear
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four scenarios where the Sustaining question comes up
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The wait-it-out posture rarely arrives as a strategic choice — it usually arrives as a
              consequence of one of these four scenarios. The right forward path depends on which one
              describes your situation.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {SCENARIOS.map(({ icon: Icon, title, body }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Engagement tiers */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud engages on the wait-it-out decision
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four engagement tiers for the Sustaining Support decision
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The same 25-year founder-level WebCenter expertise that powers EZ Cloud's modernization
              assessments applies equally to the Sustaining-vs-Market-Driven-vs-forward-path decision.
              Tiers 1-3 are Standard Consulting; Tier 4 is Forward Deployed Engineering for customers
              who choose a bridge posture and need an embedded presence through the window.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {ENGAGEMENT_TIERS.map((t) => (
                <Card key={t.label} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    {t.label}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{t.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{t.body}</p>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Engagement pricing is established per situation on a Decision Call. For customers who
              decide on the 14c upgrade as the forward path, see the{' '}
              <Link
                to="/solutions/webcenter-14c-upgrade"
                className="font-semibold text-primary hover:text-accent transition-colors"
              >
                WebCenter 14c Upgrade
              </Link>{' '}
              page.
            </p>
          </div>
        </section>

        {/* Cross-link */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the four forward paths — Sustaining, Market Driven, 14c
              upgrade, and modernization — with stack-specific considerations, eight diagnostic
              questions, and vendor evaluation checklist.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on Sustaining and Market Driven Support
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <div className="flex items-center justify-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Building2 className="w-4 h-4" aria-hidden />
              Talk through the wait-it-out math
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Talk through your Sustaining Support decision
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud and a 25-year Oracle
              WebCenter specialist. Walk through your stack, your wait-it-out window, and the
              realistic forward-path options in the context of your specific implementation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('sustaining-support-cta')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a Wait-It-Out Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default WebCenterSustainingSupportDecision;
